import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../services/api';
import InfoPanel from '../components/InfoPanel';

export default function Admin() {
  const queryClient = useQueryClient();

  // User management state
  const [newUsername, setNewUsername] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newRole, setNewRole] = useState<'analyst' | 'admin'>('analyst');

  // API key state
  const [newApiKey, setNewApiKey] = useState('');
  const [showApiKeyInput, setShowApiKeyInput] = useState(false);

  // Excluded users state
  const [newPattern, setNewPattern] = useState('');

  // Sync state
  const [syncFrom, setSyncFrom] = useState('');
  const [syncTo, setSyncTo] = useState('');
  const [syncResult, setSyncResult] = useState<string | null>(null);

  // Queries
  const { data: usersData, isLoading: usersLoading } = useQuery({
    queryKey: ['appUsers'],
    queryFn: () => api.getAppUsers(),
  });

  const { data: apiKeyData, isLoading: apiKeyLoading } = useQuery({
    queryKey: ['apiKey'],
    queryFn: () => api.getApiKey(),
  });

  const { data: excludedData, isLoading: excludedLoading } = useQuery({
    queryKey: ['excludedUsers'],
    queryFn: () => api.getExcludedUsers(),
  });

  const { data: syncHistoryData, isLoading: syncHistoryLoading } = useQuery({
    queryKey: ['syncHistory'],
    queryFn: () => api.getSyncHistory(),
    refetchInterval: 10000, // Refresh every 10 seconds to catch running syncs
  });

  // Mutations
  const createUserMutation = useMutation({
    mutationFn: () => api.createAppUser(newUsername, newPassword, newRole),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['appUsers'] });
      setNewUsername('');
      setNewPassword('');
      setNewRole('analyst');
    },
  });

  const deleteUserMutation = useMutation({
    mutationFn: (userId: number) => api.deleteAppUser(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['appUsers'] });
    },
  });

  const setApiKeyMutation = useMutation({
    mutationFn: () => api.setApiKey(newApiKey),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['apiKey'] });
      setNewApiKey('');
      setShowApiKeyInput(false);
    },
  });

  const addExcludedMutation = useMutation({
    mutationFn: () => api.addExcludedUser(newPattern),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['excludedUsers'] });
      setNewPattern('');
    },
  });

  const deleteExcludedMutation = useMutation({
    mutationFn: (patternId: number) => api.deleteExcludedUser(patternId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['excludedUsers'] });
    },
  });

  const syncMutation = useMutation({
    mutationFn: () => api.triggerSync(syncFrom || undefined, syncTo || undefined),
    onSuccess: (data) => {
      setSyncResult(
        `Sync completed: ${data.eventsDeleted || 0} deleted, ${data.eventsProcessed} processed, ${data.eventsInserted} inserted`
      );
      queryClient.invalidateQueries({ queryKey: ['syncHistory'] });
    },
    onError: (error: Error) => {
      // Check if it's a timeout/network error - sync may still be running in background
      const isTimeoutError = error.message.includes('Network Error') ||
                            error.message.includes('timeout') ||
                            error.message.includes('ECONNABORTED');
      if (isTimeoutError) {
        setSyncResult('Sync is running in the background. Check the history table below for progress.');
      } else {
        setSyncResult(`Sync failed: ${error.message}`);
      }
      queryClient.invalidateQueries({ queryKey: ['syncHistory'] });
    },
  });

  const handleCreateUser = (e: React.FormEvent) => {
    e.preventDefault();
    if (newUsername && newPassword) {
      createUserMutation.mutate();
    }
  };

  const handleSetApiKey = (e: React.FormEvent) => {
    e.preventDefault();
    if (newApiKey) {
      setApiKeyMutation.mutate();
    }
  };

  const handleAddExcluded = (e: React.FormEvent) => {
    e.preventDefault();
    if (newPattern) {
      addExcludedMutation.mutate();
    }
  };

  const handleSync = () => {
    setSyncResult(null);
    syncMutation.mutate();
  };

  return (
    <div className="space-y-8">
      <InfoPanel id="admin" title="Administration Panel">
        Manage application users, configure the analytics API key, set up excluded user patterns,
        and trigger manual data syncs. Data is automatically synced daily at 2:00 AM UTC.
      </InfoPanel>

      <h1 className="text-2xl font-bold text-gray-900">Administration</h1>

      {/* User Management */}
      <div className="card">
        <h2 className="text-lg font-semibold text-gray-700 mb-4">User Management</h2>

        <form onSubmit={handleCreateUser} className="mb-6">
          <div className="flex items-end space-x-4">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Username
              </label>
              <input
                type="text"
                value={newUsername}
                onChange={(e) => setNewUsername(e.target.value)}
                className="input"
                required
              />
            </div>
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Password
              </label>
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="input"
                required
              />
            </div>
            <div className="w-40">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Role
              </label>
              <select
                value={newRole}
                onChange={(e) => setNewRole(e.target.value as 'analyst' | 'admin')}
                className="input"
              >
                <option value="analyst">Analyst</option>
                <option value="admin">Admin</option>
              </select>
            </div>
            <button
              type="submit"
              disabled={createUserMutation.isPending}
              className="btn btn-primary"
            >
              {createUserMutation.isPending ? 'Creating...' : 'Add User'}
            </button>
          </div>
        </form>

        {usersLoading ? (
          <div className="animate-pulse h-20 bg-gray-200 rounded"></div>
        ) : (
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Username</th>
                  <th>Role</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {usersData?.users.map((user) => (
                  <tr key={user.id}>
                    <td>{user.username}</td>
                    <td>
                      <span
                        className={`px-2 py-1 text-xs rounded-full ${
                          user.role === 'admin'
                            ? 'bg-purple-100 text-purple-800'
                            : 'bg-blue-100 text-blue-800'
                        }`}
                      >
                        {user.role}
                      </span>
                    </td>
                    <td>{new Date(user.created_at).toLocaleDateString()}</td>
                    <td>
                      {user.role !== 'admin' && (
                        <button
                          onClick={() => deleteUserMutation.mutate(user.id)}
                          disabled={deleteUserMutation.isPending}
                          className="text-red-600 hover:text-red-800 text-sm"
                        >
                          Delete
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* API Key Management */}
      <div className="card">
        <h2 className="text-lg font-semibold text-gray-700 mb-4">API Key Configuration</h2>

        {apiKeyLoading ? (
          <div className="animate-pulse h-10 bg-gray-200 rounded w-64"></div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-600">
                Current API Key:{' '}
                {apiKeyData?.isSet ? (
                  <code className="bg-gray-100 px-2 py-1 rounded">{apiKeyData.apiKey}</code>
                ) : (
                  <span className="text-red-500">Not set</span>
                )}
              </span>
            </div>

            {showApiKeyInput ? (
              <form onSubmit={handleSetApiKey} className="flex items-end space-x-4">
                <div className="flex-1">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    New API Key
                  </label>
                  <input
                    type="text"
                    value={newApiKey}
                    onChange={(e) => setNewApiKey(e.target.value)}
                    className="input font-mono text-sm"
                    placeholder="Enter the analytics API key"
                    required
                  />
                </div>
                <button
                  type="submit"
                  disabled={setApiKeyMutation.isPending}
                  className="btn btn-primary"
                >
                  {setApiKeyMutation.isPending ? 'Saving...' : 'Save'}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowApiKeyInput(false);
                    setNewApiKey('');
                  }}
                  className="btn btn-secondary"
                >
                  Cancel
                </button>
              </form>
            ) : (
              <button
                onClick={() => setShowApiKeyInput(true)}
                className="btn btn-secondary"
              >
                {apiKeyData?.isSet ? 'Update API Key' : 'Set API Key'}
              </button>
            )}
          </div>
        )}
      </div>

      {/* Excluded Users */}
      <div className="card">
        <h2 className="text-lg font-semibold text-gray-700 mb-4">Excluded Users</h2>
        <p className="text-sm text-gray-500 mb-4">
          Add email addresses or domain patterns (e.g., @company.com) to exclude from analytics.
        </p>

        <form onSubmit={handleAddExcluded} className="mb-6">
          <div className="flex items-end space-x-4">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Email or Domain Pattern
              </label>
              <input
                type="text"
                value={newPattern}
                onChange={(e) => setNewPattern(e.target.value)}
                className="input"
                placeholder="e.g., @imray.com or user@example.com"
                required
              />
            </div>
            <button
              type="submit"
              disabled={addExcludedMutation.isPending}
              className="btn btn-primary"
            >
              {addExcludedMutation.isPending ? 'Adding...' : 'Add Pattern'}
            </button>
          </div>
        </form>

        {excludedLoading ? (
          <div className="animate-pulse h-20 bg-gray-200 rounded"></div>
        ) : (
          <div className="flex flex-wrap gap-2">
            {excludedData?.excludedUsers.map((excluded) => (
              <div
                key={excluded.id}
                className="flex items-center space-x-2 bg-gray-100 px-3 py-1 rounded-full"
              >
                <span className="text-sm">{excluded.pattern}</span>
                <button
                  onClick={() => deleteExcludedMutation.mutate(excluded.id)}
                  disabled={deleteExcludedMutation.isPending}
                  className="text-gray-500 hover:text-red-600"
                >
                  &times;
                </button>
              </div>
            ))}
            {excludedData?.excludedUsers.length === 0 && (
              <span className="text-sm text-gray-500">No excluded patterns</span>
            )}
          </div>
        )}
      </div>

      {/* Manual Sync */}
      <div className="card">
        <h2 className="text-lg font-semibold text-gray-700 mb-4">Manual Data Sync</h2>
        <p className="text-sm text-gray-500 mb-4">
          Trigger a manual sync from the analytics API. Data is automatically synced daily at 2:00
          AM UTC.
        </p>

        <div className="flex items-end space-x-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              From Date (optional)
            </label>
            <input
              type="date"
              value={syncFrom}
              onChange={(e) => setSyncFrom(e.target.value)}
              className="input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              To Date (optional)
            </label>
            <input
              type="date"
              value={syncTo}
              onChange={(e) => setSyncTo(e.target.value)}
              className="input"
            />
          </div>
          <button
            onClick={handleSync}
            disabled={syncMutation.isPending || !apiKeyData?.isSet}
            className="btn btn-primary"
          >
            {syncMutation.isPending ? 'Syncing...' : 'Trigger Sync'}
          </button>
        </div>

        {!apiKeyData?.isSet && (
          <p className="text-sm text-amber-600">
            Please set the API key before syncing data.
          </p>
        )}

        {syncResult && (
          <div
            className={`p-3 rounded-md text-sm mb-4 ${
              syncResult.includes('failed')
                ? 'bg-red-50 text-red-700'
                : syncResult.includes('background')
                ? 'bg-blue-50 text-blue-700'
                : 'bg-green-50 text-green-700'
            }`}
          >
            {syncResult}
          </div>
        )}

        {/* Sync History Table */}
        <h3 className="text-md font-semibold text-gray-700 mt-6 mb-3">Sync History</h3>
        {syncHistoryLoading ? (
          <div className="animate-pulse h-32 bg-gray-200 rounded"></div>
        ) : (
          <div className="table-container max-h-96 overflow-y-auto">
            <table className="table text-sm">
              <thead className="sticky top-0">
                <tr>
                  <th>Started</th>
                  <th>Status</th>
                  <th>Date Range</th>
                  <th className="text-right">Deleted</th>
                  <th className="text-right">Processed</th>
                  <th className="text-right">Inserted</th>
                  <th className="text-right">Errors</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {syncHistoryData?.history.map((record) => (
                  <tr key={record.id}>
                    <td className="whitespace-nowrap">
                      {new Date(record.started_at).toLocaleString()}
                    </td>
                    <td>
                      <span
                        className={`px-2 py-1 text-xs rounded-full ${
                          record.status === 'completed'
                            ? 'bg-green-100 text-green-800'
                            : record.status === 'failed'
                            ? 'bg-red-100 text-red-800'
                            : 'bg-yellow-100 text-yellow-800'
                        }`}
                      >
                        {record.status}
                      </span>
                    </td>
                    <td className="whitespace-nowrap text-xs">
                      {record.date_from && record.date_to
                        ? `${new Date(record.date_from).toLocaleDateString()} - ${new Date(record.date_to).toLocaleDateString()}`
                        : '-'}
                    </td>
                    <td className="text-right">{record.events_deleted.toLocaleString()}</td>
                    <td className="text-right">{record.events_processed.toLocaleString()}</td>
                    <td className="text-right">{record.events_inserted.toLocaleString()}</td>
                    <td className="text-right">
                      {record.events_errors > 0 ? (
                        <span className="text-red-600">{record.events_errors}</span>
                      ) : (
                        record.events_errors
                      )}
                    </td>
                  </tr>
                ))}
                {(!syncHistoryData?.history || syncHistoryData.history.length === 0) && (
                  <tr>
                    <td colSpan={7} className="text-center text-gray-500">
                      No sync history available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
